import { LoaderFunction, LoaderFunctionArgs, defer, redirect } from 'react-router-dom';

import { getApplicationError } from './errors';
import { ApplicationErrorCode } from '.';
import {
  ApolloClient,
  ApolloError,
  MaybeMasked,
  NormalizedCacheObject,
  OperationVariables,
  QueryOptions
} from '@apollo/client';

export interface queryOptions {
  notFoundRedirect?: string;
  suppressAuthErrors?: boolean;
}

export const query = async <Q, V extends OperationVariables, R>(
  client: ApolloClient<NormalizedCacheObject>,
  queryOptions: QueryOptions<V, Q>,
  transform: (data: MaybeMasked<Q>) => R | null | undefined | Promise<R | null | undefined>,
  opts?: queryOptions
) => {
  const { data, errors } = await client.query<Q, V>(queryOptions);

  const error = errors?.length ? new ApolloError({ graphQLErrors: errors }) : undefined;
  const applicationError = getApplicationError(error);

  if (applicationError?.code === ApplicationErrorCode.Unauthorized) {
    if (opts?.suppressAuthErrors) {
      return Promise.resolve(transform(data));
    }
    return redirect('/signin');
  } else if (applicationError?.code === ApplicationErrorCode.NotFound && opts?.notFoundRedirect) {
    return redirect(opts.notFoundRedirect);
  } else if (error) {
    throw error;
  }

  return Promise.resolve(transform(data));
};

export const merge =
  (loaders: Record<string, LoaderFunction>) => async (args: LoaderFunctionArgs) => {
    return defer({
      data: (async () => {
        // Initiate all promises. If any reject, the await will cause the entire promise to reject.
        const promises = await Promise.all(Object.values(loaders).map((loader) => loader(args)));

        // Map the promises back to their keys and return the object
        return Object.fromEntries(Object.keys(loaders).map((key, index) => [key, promises[index]]));
      })()
    });
  };
